import React from 'react';
import { Link } from 'gatsby';
import styled from 'styled-components';

import { colors, Heading, Text } from 'themes/components';
import { my, mb, ml, mr, mt, mx, px, pl, pt } from 'styled-components-spacing';
import Layout from 'components/Layout';
import SEO from 'components/SEO';

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  color: ${colors.grey5};
  ${mt({ mobile: 2, tablet: 6, desktop: 10 })};
  ${mb({ mobile: 6, desktop: 10 })};
  ${px(2)};
  p {
    color: ${colors.grey30};
    ${mt(2)};
    & > a {
      color: ${colors.teal40};
    }
  }
  h4 {
    ${mt(4)};
  }
`;

const Features = () => {
  return (
    <Layout>
      <Container>
        <Heading variant="h1">Mudstack Privacy Policy</Heading>
        <Text variant="small">Last updated: November 1, 2022</Text>
        <Text>
          Protecting your privacy is important to Mudstack. This Privacy
          Statement explains how Mudstack processes Personal Information
          collected through our website and Services. References to "Mudstack,"
          "we," or "our" means Mudstack, Inc.
        </Text>
        <Text>
          This Privacy Statement describes how we process Personal Information
          for our own purposes. We may also process Personal Information on
          behalf of our customers subject to a written contract. We do not
          control the data processing or protection practices of our customers,
          which may differ from those set out in this Privacy Statement.
        </Text>
        <Text>
          <strong>"Personal Information"</strong> is information that
          identifies, or can reasonably be linked directly or indirectly to, an
          identifiable person. Personal Information does not include information
          that is anonymous, de-identified, or aggregated, as those terms may be
          defined under applicable law. Terms that are capitalized but not
          defined in this Privacy Statement have the meanings assigned to them
          in our <Link to="/customer-terms">Customer Terms of Service</Link>.
        </Text>
        <Heading variant="h4">1. Information We Collect</Heading>
        <Text>
          We obtain Personal Information in different ways. Some Personal
          Information is collected directly from you. Other data is
          automatically collected or generated about how, when, and why you
          interact with us through our Services and other offerings, including
          data obtained through the use of cookies and other tracking
          technologies. We also obtain data about you from third parties
          including Account Administrators for Accounts owned or accessed by
          you.
        </Text>
        <Heading variant="h4">2. Information You Provide to Us</Heading>
        <Text>
          We collect information directly from you, such as when you sign up to
          use or access an Account or otherwise use our Services, use our Site
          or interact with one of our surveys, activities or with social media
          or otherwise contact us. The types of information we may collect from
          you include:
        </Text>
        <ol type="a">
          <li>
            <Text>
              Identifiers, such as your name, email addresses, IP address,
              account username, and account password.
            </Text>
          </li>
          <li>
            <Text>
              Professional information, such as your occupation and industry and
              other qualifications.
            </Text>
          </li>
          <li>
            <Text>
              Commercial information, such as details about your subscription
              plans and the Services and/or Accounts that you have purchased,
              used and/or expressed interest in.
            </Text>
          </li>
          <li>
            <Text>
              Financial account information, such as payment information you
              provide when purchasing our Services (a third party processes
              these transfers on our behalf as described below).
            </Text>
          </li>
          <li>
            <Text>
              Records of our interactions, such as correspondence, details of
              complaints and their resolutions, service records and the
              frequency at which you wish to receive marketing communications.
            </Text>
          </li>
        </ol>
        <Text>
          When you make purchases through our Site, we use third-party payment
          processor Stripe and do not store credit card or other financial
          information (other than confirmation that payment in a certain amount
          has been made). We collect contact and account information from you
          that we pass through to the payment processor to facilitate the
          transaction. For more information you can contact our providers as set
          forth in their privacy notices.
        </Text>
        <Heading variant="h4">3. Information We Obtain or Generate</Heading>
        <Text>
          We automatically collect or generate certain Personal Information
          about you based on your interactions with us, such as when your use
          our Services and Site, and we may associate that information with the
          Account through which your access the Services. The information we
          collect includes:
        </Text>
        <ol type="a">
          <li>
            <Text>
              Identifiers, such as unique user identifiers, unique device
              identifiers, IP addresses, cookie identifiers, information about
              Service subscriptions, and Account usernames.
            </Text>
          </li>
          <li>
            <Text>
              Internet or other electronic network activity information, such as
              the website domain visited; which activities and Service offerings
              are used, how, and for how long; posts, discussions, and other
              types of engagement with us, including on our blogs, discussion
              forums, or chat rooms; the use of any hyperlinks or downloadable
              content available through the offerings.
            </Text>
          </li>
          <li>
            <Text>Geolocation information.</Text>
          </li>
          <li>
            <Text>
              Electronic information, such as information about operating
              systems, device types, information about browsers, information
              relating to automatic updates and technical errors, information
              about your use of our Services and Site, including your preferred
              tools and experiences, and your engagement with Account(s)..
            </Text>
          </li>
          <li>
            <Text>
              Commercial information, such as records about interest in our
              Services or completed purchases.
            </Text>
          </li>
          <li>
            <Text>
              Inferences drawn from any of the personal and Service usage
              information available to us.
            </Text>
          </li>
          <li>
            <Text>
              We also use cookies (small text files stored by your web browser
              when you use websites) and related technologies, such as pixels
              and beacons (collectively “Cookies”), to collect and store
              information when you use our Services and Sites. To learn more,
              see our Cookie Statement.
            </Text>
          </li>
        </ol>
        <Text>
          Except as described in our Privacy Statement and our Cookie Statement,
          whether we collect data through the use of Cookies is up to you. You
          can change your mind at any time by going into your application
          settings or by clicking a link in the footer of our websites. The
          relevant link may be titled “Cookie preferences," “Privacy settings,”
          or something similar.
        </Text>
        <Text>
          In addition, we gather certain information automatically as part of
          your use of our Services and Site including third-party analytics
          tools, such as Google Analytics.
        </Text>
        <Heading variant="h4">4. How We Use Your Personal Information</Heading>
        <Text>
          We use your Personal Information mindfully, and in useful ways,
          including:
        </Text>
        <ol type="a">
          <li>
            <Text>
              To validate your identity as necessary to perform our contract
              with you and/or the Owner of an Account accessed by you.
            </Text>
          </li>
          <li>
            <Text>
              To deliver Services and other offerings, including information
              such as insights and recommendations, as necessary to perform our
              contract with you and/or the Owner of an Account accessed by you.
            </Text>
          </li>
          <li>
            <Text>
              To send you important information, such as about your Account or
              Account(s) accessed by you within the scope of our contract with
              you and/or the Owner of an Account accessed by you.
            </Text>
          </li>
          <li>
            <Text>
              To send you information you requested to the extent necessary to
              perform our contract with you or, when not necessary to perform a
              contract, in furtherance of our legitimate interest, for example
              in responding to customer inquiries.
            </Text>
          </li>
          <li>
            <Text>
              To maintain and improve the security of, and to troubleshoot any
              issues relating to, our Services as necessary to perform our
              contract with you and/or the Owner of an Account accessed by you
              or, when not necessary to perform a contract, in furtherance of
              our legitimate interest in protecting our business.
            </Text>
          </li>
          <li>
            <Text>
              To anonymize, aggregate, or de-identify your Personal Information
              so it can no longer identify you in furtherance of our legitimate
              interest in conducting research and analysis.
            </Text>
          </li>
          <li>
            <Text>
              To develop and improve our current and future offerings and your
              experience, including through the use of automated systems that
              analyze data using machine learning and other analytic techniques,
              in furtherance of our legitimate interests.
            </Text>
          </li>
          <li>
            <Text>
              To understand the ways our Services are used and who is using them
              in furtherance of our legitimate interest in improving our
              offerings and the overall User experience.
            </Text>
          </li>
          <li>
            <Text>
              To analyze information about how you interact or may interact with
              us or our Services in furtherance of our legitimate interest in
              improving our offerings and your experience with us or, where
              needed, with consent.
            </Text>
          </li>
          <li>
            <Text>
              To make strategic decisions concerning our business operations,
              including reporting on the performance of our business, in
              furtherance of our legitimate interest in making those decisions.
            </Text>
          </li>
          <li>
            <Text>
              To send you learning content, tutorials, suggestions, newsletters,
              surveys, or other information, including information that may be
              relevant to your interests or preferences, in furtherance of our
              legitimate interest in improving your customer experience.
            </Text>
          </li>
          <li>
            <Text>
              To send you promotional offers, advertising, or other marketing
              content in furtherance of our legitimate interest to engage in
              direct marketing.
            </Text>
          </li>
          <li>
            <Text>
              To detect, prevent, or otherwise address non-valid use of our
              Services such as through fraud and to protect you, Mudstack,
              and/or third parties in furtherance of our legitimate interests.
            </Text>
          </li>
          <li>
            <Text>
              To enforce, and review compliance with, the legal terms that
              govern our Services in furtherance of our legitimate interest in
              ensuring adherence to the relevant terms.
            </Text>
          </li>
          <li>
            <Text>
              To comply with our legal obligations under applicable laws.
            </Text>
          </li>
          <li>
            <Text>
              To protect the rights, safety, and property of Mudstack, you, the
              Owner(s) of Account(s) accessed by you, or any third party in
              furtherance of our legitimate interest in protecting us, our
              customers, our Users, and third parties.
            </Text>
          </li>
          <li>
            <Text>For other purposes for which we obtain your consent.</Text>
          </li>
          <li>
            <Text>
              As further described in our Services, such as in our Customer
              Terms of Service.
            </Text>
          </li>
        </ol>
        <Text>
          Where we rely on your consent to process Personal Information, you may
          withdraw your consent at any time by contacting us at the contact
          details below or as otherwise indicated to you in writing. Please note
          that your withdrawal of consent will not affect our processing
          activities based on your consent that occurred before your withdrawal.
        </Text>
        <Text>
          When we process Personal Information about you to comply with legal
          requirements or to perform our obligations under a contract with you
          and/or the Owner of an Account accessed by you, failure to provide
          such data may prevent or delay our fulfillment of these obligations.
        </Text>
        <Heading variant="h4">
          5. How We Disclose your Personal Information
        </Heading>
        <Text>
          We disclose the categories of information described above to the
          following categories of persons:
        </Text>
        <ol type="a">
          <li>
            <Text>
              Our service providers, including vendors, consultants, and other
              service providers who perform services or functions on our behalf,
              for business purposes. For example, our service providers include
              companies who support us with marketing, sales, consulting,
              communications, software maintenance and support, analytics,
              social media, market research, auditing for valid use, license
              compliance, security, user verification, localization, and payment
              processing. Our service providers process your information subject
              to contractual terms that restrict their ability to use your
              information.
            </Text>
          </li>
          <li>
            <Text>
              Our business partners with whom you interact to facilitate your
              use of our Services or Site.
            </Text>
          </li>
          <li>
            <Text>
              Social networking services and platforms to connect with or share
              information publicly, with service providers, or with your
              contacts on such services or platforms.
            </Text>
          </li>
          <li>
            <Text>
              Persons to whom we are required by law to provide information,
              such as pursuant to a subpoena or a court order.
            </Text>
          </li>
          <li>
            <Text>
              Law enforcement, legal counsel, or other reasonably necessary
              parties when we have a good faith belief that the disclosure is
              necessary to prevent or respond to fraud, defend ourselves against
              attacks, or protect the rights, property, and safety of Mudstack,
              you or Owner(s) of Account(s) accessed by you and the public.
            </Text>
          </li>
          <li>
            <Text>
              Persons involved in the consideration, negotiation, completion of
              a business transaction, including the sale, merger, consolidation,
              acquisition, change in control, transfer of substantial assets,
              bankruptcy, or reorganization, and any subsequent integration.
            </Text>
          </li>
        </ol>
        <Text>
          We may also disclose your Personal Information to other parties when
          we have your consent to do so or when you direct us to, for any
          purposes requested or permitted by you.
        </Text>
        <Text>
          We may share aggregate, anonymous, or de-identified demographic,
          statistical, and other information regarding use of our Services with
          third parties for marketing, analytics, planning, and other purposes.
          Such information will not specifically identify any particular User.
        </Text>
        <Heading variant="h4">6. How We Protect Your Data</Heading>
        <Text>
          We maintain a combination of reasonable and appropriate physical,
          administrative, and technological controls to protect your data from
          unauthorized access or malicious actions. We take reasonable steps to
          limit the access our employees, contractors, and agents have to your
          Personal Information to those who need to know this data to perform
          their assigned functions.
        </Text>
        <Heading variant="h4">7. Your Rights and Choices</Heading>
        <Text>
          You have the right to access, update, delete, receive a copy of, or
          restrict our use of your Personal Information. When we process
          Personal Information based on your consent, you have the right to
          withdraw consent any time. You may submit a deletion request by
          contacting{' '}
          <a href="mailto:privacy@mudstack.com">privacy@mudstack.com</a>. We may
          retain certain data about you for legal and internal business
          purposes, such as fraud prevention, in accordance with applicable
          laws. To request a copy of the Personal Information in your account,
          please contact{' '}
          <a href="mailto:privacy@mudstack.com">privacy@mudstack.com</a>.
          Consistent with applicable law, we may ask for proof of your identity
          before fulfilling your request, and we may ask for additional forms of
          verification depending on the nature of the personal information
          requested. Certain jurisdictions grant individuals additional rights
          with respect to their Personal Information. Depending on where you
          live or applicable law, you may also have the right to:
        </Text>
        <ol type="a">
          <li>
            <Text>Object to our processing of your Personal Information.</Text>
          </li>
          <li>
            <Text>
              Have us block or anonymize your Personal Information, as
              appropriate.
            </Text>
          </li>
          <li>
            <Text>
              Receive a copy of your Personal Information to transfer it to a
              third party.
            </Text>
          </li>
          <li>
            <Text>Opt-out of certain direct marketing communications.</Text>
          </li>
          <li>
            <Text>
              Opt-out of certain disclosures of your Personal Information to
              third parties.
            </Text>
          </li>
          <li>
            <Text>
              Lodge a complaint with the relevant data protection authority in
              the jurisdiction in which you are located.
            </Text>
          </li>
        </ol>
        <Text>
          These rights may be limited in some circumstances by local law. For
          example, even if you ask us to delete your Personal Information, we
          may need to retain it to complete transactions you have requested, to
          comply with our legal obligations, or for other limited business
          purposes as required or permitted by law. To exercise applicable
          rights, please contact Mudstack at{' '}
          <a href="mailto:privacy@mudstack.com">privacy@mudstack.com</a>. Where
          we process Personal Information on behalf of the Owner of an Account
          accessed by you, we may refer the request to the relevant Owner or
          Account Administrator and cooperate with their handling of the
          request, subject to any special contractual arrangement with that
          Owner or Account Administrator. California residents, please read the
          “Your California Privacy Rights” section for additional disclosures
          and information regarding your rights under California law.
        </Text>
        <Heading variant="h4">
          8. Mudstack's Data Storage and Retention Policies
        </Heading>
        <Text>
          We store your Personal Information and content on our servers and the
          servers of our service providers. Because we and our service providers
          maintain servers in global locations, your Personal Information may be
          transferred across national borders and stored on the servers outside
          of your country or region. We will retain your Personal Information
          for as long as necessary to provide you with the Services that you are
          using or have requested, for the establishment, exercise or defense of
          legal claims, and as needed to comply with our legal obligations. Data
          may persist in copies made for backup and business continuity purposes
          for additional time.
        </Text>
        <Heading variant="h4">
          9. Transfer your Personal Information Across National Borders
        </Heading>
        <Text>
          Because we are a global company, we transfer Personal Information
          across national borders to other countries, in compliance with the
          laws that apply to that data. Where required by law, we have put in
          place measures to adequately protect Personal Information that we
          transfer across borders. If you wish to obtain a copy of the
          safeguards that we use to protect Personal Information transferred
          across borders, please contact us using the information in the
          “Contacting Us” section below.
        </Text>
        <Heading variant="h4">
          10. Mudstack’s Marketing and Advertising Practices
        </Heading>
        <Text>
          We use analytics tools that use Cookies or other technologies to
          collect information about visitors to our Site and Services. Through
          these means, we may be able to collect information from visitors to
          our Site and Services, including websites visited, how and when
          applications are used, IP addresses, device IDs, and browser
          information.
        </Text>
        <Heading variant="h4">11. How We Protect Children</Heading>
        <Text>
          Our Services are not directed to individuals under the age of 18 and
          we do not knowingly collect information from anyone under 18. If you
          become aware that a child has provided us with Personal Information,
          please contact us at{' '}
          <a href="mailto:privacy@mudstack.com">privacy@mudstack.com</a>. If we
          become aware that a child under 18 has provided us with Personal
          Information, we will take steps to delete such information as soon as
          possible.
        </Text>
        <Heading variant="h4">
          12. Public Display of Personal Information You Give Us
        </Heading>
        <Text>
          As always, we urge you to be mindful when deciding to disclose
          Personal Information, including through our Services. Our Services
          will allow you to post information that may be viewed and downloaded
          by all Users of the Account in which such information is posted and
          may allow you to post content, including comments and “likes,” that
          can be visible to the public. In some cases, your Account
          Administrator can limit who can view or access the information and
          content you post. In other cases, the information and content will be
          available to all Users of the Account through which the information is
          posted, or even to the general public. Your name, username, profile
          image, or email address (particularly if this is your username) may be
          posted along with any message or other content you post through the
          Services.{' '}
          <strong>
            It is your responsibility to understand the settings of the Account
            through which you post information or content. If you are posting
            information or content you do not want to be disclosed publicly,
            make sure you are using an Account that allows you to understand
            and/or control who sees information or content you have posted. We
            will have no responsibility or liability to any User for any
            Personal Information, Data, or other information or content posted
            by anyone through use of the Services, including Users.
          </strong>
        </Text>
        <Heading variant="h4">13. Changes to this Privacy Statement</Heading>
        <Text>
          We may change this Privacy Statement to reflect changes in the law,
          our data handling practices, or the features of our business. The
          updated Privacy Statement will be available at{' '}
          <a href="https://www.mudstack.com/privacy-policy">
            mudstack.com/privacy-policy
          </a>
          . We will show the date the Privacy Statement was last updated to help
          you know when we change it. If we make material changes to our Privacy
          Statement, we will endeavor to provide you with notice before provide
          you with notice before such changes take effect, such as through
          prominent notice on our website or services or by email.
        </Text>
        <Heading variant="h4">14. Contacting Us</Heading>
        <Text>
          To contact us, please use the following contact information. When you
          do so, please tell us the name of the Owner of the Account about which
          you are contacting us:
        </Text>
        <Text>
          By email:{' '}
          <a href="mailto:privacy@mudstack.com">privacy@mudstack.com</a>
        </Text>
        <Text>By postal mail:</Text>
        <Text>
          Mudstack, Inc. <br />
          Privacy Questions <br />
          6000 Foxberry Ln <br /> Roswell, GA 30075
        </Text>
        <Heading variant="h4">15. Your California Privacy Rights</Heading>
        <Text>
          If you reside in California, you have rights to access the specific
          pieces of your information, the right to know details about our
          processing of your information, the right to request deletion of your
          Personal Information, and the right to not be denied goods or services
          for exercising these rights. You can exercise these rights, request
          deletion of your Personal Information and/or request a copy of the
          Personal Information in your account by emailing us at{' '}
          <a href="mailto:privacy@mudstack.com">privacy@mudstack.com</a>.
        </Text>
      </Container>
    </Layout>
  );
};

export const Head = () => (
  <SEO
    title="Mudstack Privacy Policy"
    description="Digital asset management, review & collaboration and version control designed specificall for video game art teams."
  />
);

export default Features;
